import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  VideoCameraFrontOutlined as VideoCallIcon,
  PendingOutlined as PendingIcon,
  DirectionsRunOutlined as OnGoingIcon,
  CheckCircleOutlineOutlined as FinishedIcon,
  CloseRounded as CloseRoundedIcon,
} from "@mui/icons-material";

import { useTranslation } from "react-i18next"; // Mantener la importación para asegurar la carga de configuraciones de i18n

const ActivityDrawer = ({
  open,
  onClose,
  editActivityId,
  newActivity,
  handleActivityChange,
  handleActivitySubmit,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const accent = "#7c6bf1";
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 430,
          maxWidth: "100%",
          borderTopLeftRadius: 24,
          borderBottomLeftRadius: 24,
          borderLeft: `1px solid ${alpha(accent, 0.12)}`,
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.common.white,
            0.96,
          )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
        },
      }}
    >
      <Box sx={{ p: 1.4, borderBottom: `1px solid ${alpha(accent, 0.1)}` }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 800,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              CRM
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: "1.35rem", lineHeight: 1 }}>
              {editActivityId ? t("Activities.Edit") : t("Activities.Add")}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: "0.8rem", color: "text.secondary" }}>
              Seguimiento, estado y próxima acción
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              border: "1px solid",
              borderColor: alpha(accent, 0.12),
              backgroundColor: alpha(theme.palette.common.white, 0.76),
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          display: "grid",
          gap: 1,
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.common.white, 0.82),
            boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.04)}`,
            "& fieldset": {
              borderColor: alpha(accent, 0.12),
            },
          },
        }}
      >

        <TextField
          name="subject"
          label={t("Activities.Subget")}
          value={newActivity.subject}
          onChange={handleActivityChange}
          fullWidth
          margin="dense"
          InputProps={{ readOnly: true }}
          sx={{
            "& .MuiInputBase-input": {
              color: "#34afe2",
              fontWeight: "800",
              fontSize: "20px",
            },
          }}
        />

        <FormControl fullWidth margin="dense">
          <InputLabel id="activityType-label">
            {t("Activities.Activities")}
          </InputLabel>
          <Select
            labelId="activityType-label"
            name="activityType"
            value={newActivity.activityType}
            onChange={handleActivityChange}
          >
            <MenuItem value="Llamada">
              <PhoneIcon sx={{ mr: 1, color: "red" }} />
              {t("Activities.Llamada")}
            </MenuItem>
            <MenuItem value="Email">
              <EmailIcon sx={{ mr: 1, color: "blue" }} />
              {t("Activities.Email")}
            </MenuItem>
            <MenuItem value="Enviar presentación">
              <DescriptionIcon sx={{ mr: 1, color: "orange" }} />
              {t("Activities.Enviar presentación")}
            </MenuItem>
            <MenuItem value="Video Call">
              <VideoCallIcon sx={{ mr: 1, color: "#c896ff" }} />
              {t("Activities.Video Call")}
            </MenuItem>
            <MenuItem value="Visita Cliente">
              <BusinessIcon sx={{ mr: 1, color: "green" }} />
              {t("Activities.Visita Cliente")}
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense">
          <InputLabel id="activityStatus-label">
            {t("Activities.statement")}
          </InputLabel>
          <Select
            labelId="activityStatus-label"
            name="status"
            value={newActivity.status}
            onChange={handleActivityChange}
          >
            <MenuItem value="Pending">
              <PendingIcon sx={{ mr: 1, color: "#dc3545" }} />
              {t("Activities.Pending")}
            </MenuItem>
            <MenuItem value="On going">
              <OnGoingIcon sx={{ mr: 1, color: "#3174ad" }} />
              {t("Activities.On going")}
            </MenuItem>
            <MenuItem value="Finished">
              <FinishedIcon sx={{ mr: 1, color: "#34afe2" }} />
              {t("Activities.Finished")}
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          name="notes"
          label={t("Activities.Notas")}
          value={newActivity.notes}
          onChange={handleActivityChange}
          fullWidth
          margin="dense"
        />

        <TextField
          name="activityDate"
          label={t("Activities.FechaActividad")}
          type="date"
          value={newActivity.activityDate}
          onChange={handleActivityChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          margin="dense"
        />

        <TextField
          name="gMeetId"
          label={t("Activities.GoogleMeetLink")}
          value={newActivity.gMeetId}
          onChange={handleActivityChange}
          fullWidth
          margin="dense"
        />

        <Button
          onClick={handleActivitySubmit}
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${accent} 0%, ${alpha(
              accent,
              0.82,
            )} 100%)`,
            color: theme.palette.getContrastText(accent),
            mt: 2,
            width: "100%",
            padding: "11px 0",
            fontWeight: "800",
            borderRadius: 999,
            boxShadow: `0 14px 30px ${alpha(accent, 0.24)}`,
          }}
        >
          {editActivityId ? t("Activities.Actualizar") : t("Activities.Add")}
        </Button>
      </Box>
    </Drawer>
  );
};

export default ActivityDrawer;
